import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences } from 'app/pwa/app.cerebral';
import { useText, useLink } from 'common/hooks';
import {
  Wrapper,
  InnerWrapper,
  Text,
  TimelineItem,
  TimelineSubItem,
  SubItemWrapper,
} from './elements';

// TODO: Remove array as survey no longer exists (so only one tool)
const toolOrdering = ['challenge'];

const deps = {
  computedCampaigns: state.account.timeline.computedCampaigns,
  subItemsShowing: state.account.timeline.subItemsShowing,
  toggleShowTimelineSubItems: sequences`account.timeline.toggleShowTimelineSubItems`,
  showExplanationModal: sequences`account.timeline.showExplanationModal`,
  redirectToCampaignPage: sequences`account.timeline.redirectToCampaignPage`,
};

export function CampaignList({
  computedCampaigns,
  subItemsShowing,
  toggleShowTimelineSubItems,
  showExplanationModal,
  redirectToCampaignPage,
}: typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const link = useLink();

  return (
    <Wrapper>
      {(Object.keys(computedCampaigns) as Array<keyof typeof computedCampaigns>)
        .sort()
        .filter(
          campaignPeriod =>
            Object.keys(computedCampaigns[campaignPeriod]).length > 0
        )
        .map(campaignPeriod => (
          <InnerWrapper key={campaignPeriod}>
            <Text>{t(`account.timeline.${campaignPeriod}`)}</Text>
            {computedCampaigns[campaignPeriod].map(campaign => (
              <div key={campaign.id}>
                <TimelineItem
                  smallText={`${campaign.startDate} - ${campaign.endDate} (${campaign.organisationName})`}
                  buttonText={t('account.timeline.joined')}
                  onClick={() => {
                    toggleShowTimelineSubItems({
                      campaignId: campaign.id,
                    });
                  }}
                  expanded={subItemsShowing === campaign.id}
                >
                  {campaign.campaignName}
                </TimelineItem>
                {subItemsShowing === campaign.id && (
                  <SubItemWrapper>
                    {campaign.campaignPageUrl && (
                      <TimelineSubItem
                        number={1}
                        title={t(`account.timeline.website`, {
                          campaignName: campaign.campaignName,
                        })}
                        linkOneText={t('account.timeline.see_more')}
                        linkOneOnClick={() => {
                          redirectToCampaignPage({
                            url: campaign.campaignPageUrl,
                          });
                        }}
                        state={'during'}
                        border
                      />
                    )}
                    {Object.keys(campaign.tools)
                      .sort((a, b) => {
                        if (
                          campaign.tools[a].startDatetime ===
                          campaign.tools[b].startDatetime
                        ) {
                          return (
                            toolOrdering.indexOf(campaign.tools[a].type) -
                            toolOrdering.indexOf(campaign.tools[b].type)
                          );
                        }

                        return (
                          campaign.tools[a].startDatetime -
                          campaign.tools[b].startDatetime
                        );
                      })
                      .map((toolKey, index) => {
                        const {
                          type,
                          routeKey,
                          state: toolState,
                          endDatetime,
                          startDate,
                        } = campaign.tools[toolKey];

                        const tool = type;

                        const title = t(
                          `tools.${tool}.${campaign.topic}.title`
                        );

                        return (
                          <TimelineSubItem
                            key={toolKey}
                            number={
                              campaign.campaignPageUrl ? index + 2 : index + 1
                            }
                            title={title}
                            text={
                              endDatetime < Date.now()
                                ? t('account.timeline.completed')
                                : startDate
                            }
                            linkOneText={t('account.timeline.learn_more')}
                            linkOneOnClick={() => {
                              showExplanationModal({
                                toShow: `${tool}.${campaign.topic}`,
                              });
                            }}
                            linkTwoText={t(`account.timeline.go_to`, {
                              toolName: title,
                            })}
                            linkTwoUrl={link(`${type}/${routeKey}`)}
                            state={toolState}
                            border={
                              index + 1 !== Object.keys(campaign.tools).length
                            }
                          />
                        );
                      })}
                  </SubItemWrapper>
                )}
              </div>
            ))}
          </InnerWrapper>
        ))}
    </Wrapper>
  );
}

export default connect(deps, CampaignList);
